import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { get, post } from "@rails/request.js"
import { setHidden } from "../lib/display_toggle"

/**
 * "Create Professional Image" (#12982; every portrait surface, #13320) —
 * the AI headshot flow beside an agent portrait slot: Lead Defaults Display
 * Photo, Profile avatar, Website hero, Business Card picture, Calendar logo.
 * One controller for all of them; the destination only changes the values.
 *
 * Wrap the button, the slot's file input (looked up by inputSelector) and a
 * user_settings/_headshot_generation_modal partial
 * (shared/_professional_image_tools renders all three):
 *
 *   div[data-controller="headshot-generation"
 *       data-headshot-generation-create-url-value="/headshot_generations"
 *       data-headshot-generation-saved-photo-url-value="<saved portrait variant url or blank>"
 *       data-headshot-generation-saved-source-kind-value="display_photo | profile_photo | website_hero | …"
 *       data-headshot-generation-input-selector-value="#user_display_photo"
 *       data-headshot-generation-no-source-reason-value="Choose or upload a Display Photo first."
 *       data-headshot-generation-remaining-value="3"
 *       data-headshot-generation-unavailable-reason-value="<blank, or why the button is disabled>"]
 *
 * States: idle → generating (POST + poll) → succeeded | failed. Everything
 * here is STAGED: "Use This Photo" writes the generated File into the slot's
 * input (via DataTransfer) and dispatches `change`, so the standard photo-crop
 * modal opens on it and image-preview / card-dirty react — exactly what a
 * picked file does. Only the page's own save persists anything: a form's Save
 * button (the hidden headshot_generation_id field travels with that submit so
 * the server can mark the generation accepted), or, for the Profile avatar,
 * the crop modal's Apply (profile_photo_controller uploads then and reads the
 * id from the input's dataset, which this controller stamps). Both are
 * cleared when the agent picks a different file afterwards.
 *
 * Source: the file currently in the input when there is one (a new, unsaved
 * upload), else the destination's saved portrait (savedPhotoUrl, sent as
 * savedSourceKind). The button is disabled — with the reason as its tooltip
 * and aria-label — while neither exists, while the allowance is used up, or
 * while the server said the feature is unavailable.
 *
 * Stale protection: the source is fingerprinted when generation starts; if
 * the agent changes the photo while a result is pending, the result is shown
 * as "from your previous photo" and cannot be used — only "Try again" (from the
 * new photo) or "Keep original". A poll result for a generation this modal is
 * no longer waiting on is ignored (session counter, like photo-crop).
 */
export default class extends Controller {
  static targets = ["openButton", "modal", "original", "result", "resultStage", "placeholder",
    "progress", "progressLabel", "error", "errorText", "nextStep", "primaryButton", "retryButton",
    "keepButton", "remaining", "generationField", "sourceNote", "attire"]

  static values = {
    createUrl: String,
    savedPhotoUrl: String,
    savedSourceKind: { type: String, default: "display_photo" },
    inputSelector: { type: String, default: "#user_display_photo" },
    noSourceReason: { type: String, default: "Choose or upload a Display Photo first." },
    remaining: { type: Number, default: 0 },
    unavailableReason: String,
    pollIntervalMs: { type: Number, default: 2500 },
    pollTimeoutMs: { type: Number, default: 240000 },
  }

  static NO_SOURCE_REASON = "Choose or upload a Display Photo first."
  static GENERATION_DATASET_KEY = "headshotGenerationId"
  static SAVED_EVENT = "profile-photo:uploaded"
  static EXHAUSTED_REASON = "You have used all of your professional images for this month."
  static STALE_REASON = "This image was made from your previous photo. Try again to use the new one, or keep the original."
  static NETWORK_REASON = "We couldn't reach the server. Check your connection and try again."
  static TIMEOUT_REASON = "The image service took too long. Try again."
  static CREATE_LABEL = "Create Image"
  static USE_LABEL = "Use This Photo"
  static RESULT_FILENAME = "professional-image.jpg"

  connect() {
    this.session = 0
    this.state = "idle"
    this.generation = null
    this.resultBlob = null
    this.resultUrl = null
    this.originalUrl = null
    this.sourceFingerprint = null
    this.pollTimer = null
    this.acceptedFile = null
    this.acceptedAt = null
    this.boundInputChanged = () => this.inputChanged()
    this.boundPhotoSaved = (event) => this.photoSaved(event)
    this.input?.addEventListener("change", this.boundInputChanged)
    this.input?.addEventListener(this.constructor.SAVED_EVENT, this.boundPhotoSaved)
    this.syncOpenButton()
  }

  disconnect() {
    this.input?.removeEventListener("change", this.boundInputChanged)
    this.input?.removeEventListener(this.constructor.SAVED_EVENT, this.boundPhotoSaved)
    this.stopPolling()
    this.revokeUrls()
  }

  // ── Opening ─────────────────────────────────────────────────────────

  open() {
    const source = this.currentSource()
    if (!source) return
    this.resetForNewSource(source)
    this.bootstrapModal.show()
  }

  // The primary footer button is "Create Image" until a usable result exists,
  // then "Use This Photo" — one primary action at a time (MODAL-1/2).
  primaryAction() {
    if (this.state === "succeeded" && !this.stale) {
      this.use()
    } else if (this.state !== "generating") {
      this.generate()
    }
  }

  // ── Generation ──────────────────────────────────────────────────────

  // #13421 — the modal's one option. The checkbox's value is the `business`
  // key; unchecked (or no checkbox in the markup) is `keep`, the v1 behaviour.
  attireChoice() {
    const box = this.hasAttireTarget ? this.attireTarget : null
    return box && box.checked ? box.value : "keep"
  }

  async generate() {
    const source = this.currentSource()
    if (!source) {
      this.showError(this.noSourceReason)
      return
    }
    if (this.remainingValue <= 0) {
      this.showError(this.constructor.EXHAUSTED_REASON)
      return
    }
    this.resetForNewSource(source)
    this.setState("generating")
    const session = this.session

    const body = new FormData()
    body.append("request_key", this.newRequestKey())
    body.append("source_kind", source.kind)
    body.append("attire", this.attireChoice())
    if (source.kind === "upload") body.append("source_image", source.file, source.file.name)

    let response
    try {
      response = await post(this.createUrlValue, { body, responseKind: "json" })
    } catch (e) {
      if (session !== this.session) return
      console.error("Headshot generation request failed:", e)
      this.fail(this.constructor.NETWORK_REASON)
      return
    }
    if (session !== this.session) return

    const payload = await this.readJson(response)
    if (!response.ok) {
      // A duplicate (another tab already generating) still carries the row to
      // poll; every other refusal is final for this attempt.
      if (payload?.generation && payload.error_code === "already_running") {
        this.trackGeneration(payload.generation, session)
        return
      }
      if (typeof payload?.remaining_this_month === "number") this.remainingValue = payload.remaining_this_month
      this.fail(payload?.error_message || this.constructor.NETWORK_REASON)
      return
    }
    this.trackGeneration(payload, session)
  }

  trackGeneration(generation, session) {
    this.generation = generation
    if (typeof generation.remaining_this_month === "number") this.remainingValue = generation.remaining_this_month
    this.pollStartedAt = Date.now()
    this.applyGenerationStatus(generation, session)
  }

  applyGenerationStatus(generation, session) {
    if (session !== this.session) return
    this.generation = generation
    switch (generation.status) {
      case "queued":
      case "running":
        this.schedulePoll(session)
        break
      case "succeeded":
      case "accepted":
        this.loadResult(generation, session)
        break
      default:
        this.fail(generation.failure_message || this.constructor.NETWORK_REASON)
    }
  }

  schedulePoll(session) {
    if (Date.now() - this.pollStartedAt > this.pollTimeoutMsValue) {
      this.fail(this.constructor.TIMEOUT_REASON)
      return
    }
    this.stopPolling()
    this.pollTimer = setTimeout(() => this.poll(session), this.pollIntervalMsValue)
  }

  async poll(session) {
    if (session !== this.session || !this.generation) return
    try {
      const response = await get(`${this.createUrlValue}/${this.generation.id}`, { responseKind: "json" })
      if (session !== this.session) return
      if (!response.ok) {
        this.fail(this.constructor.NETWORK_REASON)
        return
      }
      this.applyGenerationStatus(await this.readJson(response), session)
    } catch (e) {
      if (session !== this.session) return
      console.error("Headshot generation poll failed:", e)
      this.fail(this.constructor.NETWORK_REASON)
    }
  }

  async loadResult(generation, session) {
    try {
      const response = await get(generation.result_url)
      if (session !== this.session) return
      if (!response.ok) {
        this.fail(this.constructor.NETWORK_REASON)
        return
      }
      const blob = await response.response.blob()
      if (session !== this.session) return
      this.resultBlob = blob
      if (this.resultUrl) URL.revokeObjectURL(this.resultUrl)
      this.resultUrl = URL.createObjectURL(blob)
      this.resultTarget.src = this.resultUrl
      this.setState("succeeded")
    } catch (e) {
      if (session !== this.session) return
      console.error("Headshot result could not be loaded:", e)
      this.fail(this.constructor.NETWORK_REASON)
    }
  }

  // ── Accepting ───────────────────────────────────────────────────────

  // Hands the generated file to the slot's input once the modal has closed
  // (nested modals never stack): photo-crop's fileSelected then opens the
  // standard crop modal on it, image-preview paints the slot, card-dirty
  // enables Save. The generation id rides the next submit (hidden field) and
  // sits on the input's dataset for an instant-upload slot (Profile avatar).
  use() {
    if (!this.resultBlob || this.stale) return
    this.pendingAccept = true
    this.bootstrapModal.hide()
  }

  modalHidden() {
    this.stopPolling()
    this.session += 1
    if (this.state === "generating") this.setState("idle")
    if (this.pendingAccept) {
      this.pendingAccept = false
      this.writeResultIntoInput()
    }
  }

  writeResultIntoInput() {
    const input = this.input
    if (!input || !this.resultBlob) return
    const file = new File([this.resultBlob], this.constructor.RESULT_FILENAME, { type: this.resultBlob.type || "image/jpeg" })
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.acceptedFile = file
    this.acceptedAt = Date.now()
    input.files = transfer.files
    this.recordGenerationId(String(this.generation?.id ?? ""))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  recordGenerationId(id) {
    if (this.hasGenerationFieldTarget) this.generationFieldTarget.value = id
    const input = this.input
    if (!input) return
    if (id) input.dataset[this.constructor.GENERATION_DATASET_KEY] = id
    else delete input.dataset[this.constructor.GENERATION_DATASET_KEY]
  }

  // change-> on the slot's input (after photo-crop / image-preview /
  // user-edit ran). Still "ours" when the input holds the file we wrote, or
  // photo-crop's export of it (it renames every export profile-photo.jpg/.png
  // and re-fires change right after Apply). Anything else — a fresh pick, a
  // cancelled crop restoring the previous file, an emptied input — means the
  // generation is no longer what will be saved, so its id leaves the form.
  inputChanged() {
    const file = this.input?.files?.[0]
    const ours = this.acceptedFile && file &&
      (file === this.acceptedFile || (this.recentlyAccepted() && /^profile-photo\.(jpe?g|png)$/i.test(file.name)))
    if (ours) {
      this.acceptedFile = file
    } else {
      this.acceptedFile = null
      this.acceptedAt = null
      this.recordGenerationId("")
    }
    if (this.state === "generating" || this.state === "succeeded") {
      const source = this.currentSource()
      this.stale = !source || this.fingerprint(source) !== this.sourceFingerprint
      if (this.state === "succeeded") this.renderSucceeded()
    }
    this.syncOpenButton()
  }

  recentlyAccepted() {
    return this.acceptedAt != null && Date.now() - this.acceptedAt < 10 * 60 * 1000
  }

  // profile-photo:uploaded on an instant-upload slot: the destination's saved
  // portrait is now the file that was just uploaded (its preview URL comes in
  // the event), the accepted result has been consumed, and the input is empty
  // again — so the button works from the NEW saved photo, never a stale one.
  photoSaved(event) {
    const previewUrl = event?.detail?.previewUrl
    if (previewUrl) this.savedPhotoUrlValue = previewUrl
    this.acceptedFile = null
    this.acceptedAt = null
    this.recordGenerationId("")
    this.syncOpenButton()
  }

  // ── State & rendering ───────────────────────────────────────────────

  resetForNewSource(source) {
    this.stopPolling()
    this.session += 1
    this.generation = null
    this.stale = false
    this.resultBlob = null
    this.sourceFingerprint = this.fingerprint(source)
    this.revokeUrls()
    if (source.kind === "upload") {
      this.originalUrl = URL.createObjectURL(source.file)
      this.originalTarget.src = this.originalUrl
    } else {
      this.originalTarget.src = source.url
    }
    this.clearError()
    this.setState("idle")
  }

  setState(state) {
    this.state = state
    const generating = state === "generating"
    const succeeded = state === "succeeded"
    setHidden(this.progressTarget, !generating)
    setHidden(this.placeholderTarget, succeeded)
    setHidden(this.resultTarget, !succeeded)
    setHidden(this.retryButtonTarget, !(succeeded || state === "failed"))
    this.retryButtonTarget.disabled = generating || this.remainingValue <= 0
    this.keepButtonTarget.disabled = false
    if (succeeded) {
      this.renderSucceeded()
    } else {
      setHidden(this.nextStepTarget, true)
      this.primaryButtonTarget.textContent = ""
      this.primaryButtonTarget.append(this.icon("ph-magic-wand"), this.constructor.CREATE_LABEL)
      this.primaryButtonTarget.disabled = generating || this.remainingValue <= 0
      this.primaryButtonTarget.setAttribute("aria-busy", generating ? "true" : "false")
    }
    if (generating) this.clearError()
    this.renderRemaining()
  }

  renderSucceeded() {
    this.primaryButtonTarget.textContent = ""
    this.primaryButtonTarget.append(this.icon("ph-check"), this.constructor.USE_LABEL)
    this.primaryButtonTarget.setAttribute("aria-busy", "false")
    this.primaryButtonTarget.disabled = this.stale
    setHidden(this.nextStepTarget, this.stale)
    if (this.stale) this.showError(this.constructor.STALE_REASON)
    else this.clearError()
  }

  fail(message) {
    this.stopPolling()
    this.setState("failed")
    this.showError(message)
  }

  renderRemaining() {
    if (!this.hasRemainingTarget) return
    const n = this.remainingValue
    this.remainingTarget.textContent = n > 0
      ? `You have ${n} professional ${n === 1 ? "image" : "images"} left this month.`
      : "You have no professional images left this month."
  }

  // Disabled + tooltip + aria-label with the reason (BUTTON-4 / A11Y-1).
  syncOpenButton() {
    if (!this.hasOpenButtonTarget) return
    const button = this.openButtonTarget
    let reason = this.unavailableReasonValue || ""
    if (!reason && !this.currentSource()) reason = this.noSourceReason
    if (!reason && this.remainingValue <= 0) reason = this.constructor.EXHAUSTED_REASON
    button.disabled = Boolean(reason)
    if (reason) {
      button.dataset.glTooltip = reason
      button.setAttribute("aria-label", `Create Professional Image — ${reason}`)
    } else {
      delete button.dataset.glTooltip
      button.removeAttribute("aria-label")
    }
    if (this.hasSourceNoteTarget) {
      this.sourceNoteTarget.textContent = reason || ""
      setHidden(this.sourceNoteTarget, !reason)
    }
  }

  showError(message) {
    this.errorTextTarget.textContent = message
    setHidden(this.errorTarget, false)
  }

  clearError() {
    this.errorTextTarget.textContent = ""
    setHidden(this.errorTarget, true)
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  get input() {
    return document.querySelector(this.inputSelectorValue)
  }

  get bootstrapModal() {
    return Modal.getOrCreateInstance(this.modalTarget)
  }

  get noSourceReason() {
    return this.noSourceReasonValue || this.constructor.NO_SOURCE_REASON
  }

  // { kind: "upload", file } | { kind: <savedSourceKind>, url } | null — the
  // saved kind is the destination's own key (display_photo, profile_photo, …),
  // which the server resolves to that saved portrait and nothing else.
  currentSource() {
    const file = this.input?.files?.[0]
    if (file) return { kind: "upload", file }
    if (this.savedPhotoUrlValue) return { kind: this.savedSourceKindValue, url: this.savedPhotoUrlValue }
    return null
  }

  fingerprint(source) {
    if (source.kind === "upload") {
      const f = source.file
      return `upload:${f.name}:${f.size}:${f.lastModified}`
    }
    return `${source.kind}:${source.url}`
  }

  newRequestKey() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
    return `hs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }

  async readJson(response) {
    try {
      return await response.json
    } catch {
      return null
    }
  }

  icon(name) {
    const i = document.createElement("i")
    i.className = `ph ${name} me-1`
    i.setAttribute("aria-hidden", "true")
    return i
  }

  stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  revokeUrls() {
    if (this.originalUrl) { URL.revokeObjectURL(this.originalUrl); this.originalUrl = null }
    if (this.resultUrl) { URL.revokeObjectURL(this.resultUrl); this.resultUrl = null }
  }
}
